import { LeverageParameters } from '@katanaperps/katana-perps-sdk';
import { Reducer } from 'redux';

import { ApiMarket } from '#/api/types';
import { MarketSessionInfo } from '#/components/markets/intermittent/intermittentMarkets';
import { arraysEqual, setEqual, stabilizeMapEntries } from '#/lib/utils';

import {
  debugUpdateMarketsStatus,
  loadHiddenMarket,
  loadMarkets,
} from './listedMarketsActions';
import { PerMarketStringMap } from './listedMarketsTypes';
import { calculateTickOrStepSize } from './listedMarketsUtils';

// Actions

type ListedMarketsAction = ReturnType<
  typeof loadMarkets | typeof loadHiddenMarket | typeof debugUpdateMarketsStatus
>;

// Reducer

export type TickSizes = {
  [market: string]:
    | {
        inPip: number;
        asDecimal: string;
        /** Note that 0 decimals is also valid */
        visibleDecimals: number;
      }
    | undefined;
};

export type StepSizes = {
  [market: string]:
    | {
        inPip: number;
        asDecimal: string;
        visibleDecimals: number;
      }
    | undefined;
};

/**
 * Static subset of {@link ApiMarket} (no prices/volumes — those change on
 * every API response and belong to tickers). Entry references stay stable
 * across market reloads, so components re-render only when a market's
 * configuration or session state actually changes.
 */
export type MarketData = Pick<
  ApiMarket,
  | 'market'
  | 'type'
  | 'status'
  | 'baseAsset'
  | 'quoteAsset'
  | 'marginType'
  | 'tradingSessionStatus'
  | 'approximateNextCloseTime'
  | 'approximateNextOpenTime'
  | 'tickSize'
  | 'stepSize'
  | 'takerOrderMinimum'
  | 'makerOrderMinimum'
  | 'minimumPositionSize'
  | 'maximumPositionSize'
  | 'basePositionSize'
  | 'incrementalPositionSize'
  | 'initialMarginFraction'
  | 'maintenanceMarginFraction'
  | 'incrementalInitialMarginFraction'
  | 'takerFeeRate'
  | 'makerFeeRate'
>;

type ListedMarketsState = {
  defaultMarket?: string;

  loadedForLoggedIn: null | boolean; // null - when not loaded at all

  /**
   * When the markets API was last loaded (null before the first load) —
   * polling schedules from it, so websocket triggered reloads push the next
   * poll out instead of stacking an extra fetch
   */
  loadedAtMs: null | number;

  // marketSymbol as "ETH-USDC"
  listedMarketsList: string[];
  listedTokensSet: null | Set<string>;

  /** Pinned markets will be displayed first on Tickers screen */
  marketsPinPosition?: {
    [market: string]: number;
  };

  marketsLeverageParams?: {
    [market: string]: LeverageParameters;
  };

  /** Margin type and intermittent market session info per market */
  marketsTradingInfo?: {
    [market: string]: MarketSessionInfo;
  };

  /** Static data per market (listed + hidden), see {@link MarketData} */
  marketsData: {
    [market: string]: MarketData;
  };

  /**
   * Minimum price increment change in quote terms
   *
   * Per market tick size (Number of decimals for price),
   *  if not defined we fall back to full 8 decimals (default exchange decimals)
   * More https://docs.google.com/document/d/1RPJAuAxGR0s702ldW9LqlYvHUMo9Rox_lSRyJIZeH0k/edit
   */
  tickSizes: TickSizes;

  /**
   * Minimum quantity increment change in base terms
   *
   * Per market step size (Number of decimals for price),
   *  if not defined we fall back to full 8 decimals (default exchange decimals)
   * More https://docs.google.com/document/d/1RPJAuAxGR0s702ldW9LqlYvHUMo9Rox_lSRyJIZeH0k/edit
   */
  stepSizes: StepSizes;

  /** Minimum order size (in base) per market map */
  takerOrderMinimums: PerMarketStringMap;

  /** Maximum positions size (in base) per market map */
  maximumPositionSizes: PerMarketStringMap;

  /** Active trading markets @see {@link ApiMarket.status} */
  // enabledTradingMarkets: null | Set<string>;
  /** Markets with cancels enabled @see {@link ApiMarket.status} */
  // enabledCancelsMarkets: null | Set<string>;
  /** When a market’s status is limitMaker in the /markets endpoint, the Buy/Sell panel should go into post-only mode */
  // limitMakerOnlyMarketsSet: null | Set<string>;
};

const defaultState: ListedMarketsState = {
  defaultMarket: undefined,

  loadedForLoggedIn: null,
  loadedAtMs: null,

  listedMarketsList: [],
  listedTokensSet: null,

  marketsPinPosition: undefined,
  tickSizes: {},
  stepSizes: {},

  takerOrderMinimums: {},
  maximumPositionSizes: {},

  marketsLeverageParams: {},
  marketsTradingInfo: {},
  marketsData: {},

  // enabledTradingMarkets: null,
  // enabledCancelsMarkets: null,
  // limitMakerOnlyMarketsSet: null,
};

function transformMarketLeverageParams(market: ApiMarket) {
  return {
    maximumPositionSize: market.maximumPositionSize,
    initialMarginFraction: market.initialMarginFraction,
    maintenanceMarginFraction: market.maintenanceMarginFraction,
    basePositionSize: market.basePositionSize,
    incrementalPositionSize: market.incrementalPositionSize,
    incrementalInitialMarginFraction: market.incrementalInitialMarginFraction,
  };
}

function transformMarketSessionInfo(market: ApiMarket): MarketSessionInfo {
  return {
    marginType: market.marginType,
    type: market.type,
    tradingSessionStatus: market.tradingSessionStatus,
    approximateNextCloseTime: market.approximateNextCloseTime,
    approximateNextOpenTime: market.approximateNextOpenTime,
  };
}

// Explicit field copy — spreading the whole ApiMarket would pull in the
// dynamic price/volume fields and break entry reference stability
function transformMarketData(market: ApiMarket): MarketData {
  return {
    market: market.market,
    type: market.type,
    status: market.status,
    baseAsset: market.baseAsset,
    quoteAsset: market.quoteAsset,
    marginType: market.marginType,
    tradingSessionStatus: market.tradingSessionStatus,
    approximateNextCloseTime: market.approximateNextCloseTime,
    approximateNextOpenTime: market.approximateNextOpenTime,
    tickSize: market.tickSize,
    stepSize: market.stepSize,
    takerOrderMinimum: market.takerOrderMinimum,
    makerOrderMinimum: market.makerOrderMinimum,
    minimumPositionSize: market.minimumPositionSize,
    maximumPositionSize: market.maximumPositionSize,
    basePositionSize: market.basePositionSize,
    incrementalPositionSize: market.incrementalPositionSize,
    initialMarginFraction: market.initialMarginFraction,
    maintenanceMarginFraction: market.maintenanceMarginFraction,
    incrementalInitialMarginFraction: market.incrementalInitialMarginFraction,
    takerFeeRate: market.takerFeeRate,
    makerFeeRate: market.makerFeeRate,
  };
}

export const listedMarketsReducer: Reducer<
  ListedMarketsState,
  ListedMarketsAction
> = (state = defaultState, action): ListedMarketsState => {
  switch (action.type) {
    case 'LOAD_HIDDEN_MARKET': {
      return {
        ...state,
        maximumPositionSizes: {
          ...state.maximumPositionSizes,
          [action.market.market]: action.market.maximumPositionSize,
        },
        stepSizes: {
          ...state.stepSizes,
          [action.market.market]: calculateTickOrStepSize(
            action.market.stepSize
          ),
        },
        takerOrderMinimums: {
          ...state.takerOrderMinimums,
          [action.market.market]: action.market.takerOrderMinimum,
        },
        tickSizes: {
          ...state.tickSizes,
          [action.market.market]: calculateTickOrStepSize(
            action.market.tickSize
          ),
        },
        marketsLeverageParams: {
          ...state.marketsLeverageParams,
          [action.market.market]: transformMarketLeverageParams(action.market),
        },
        marketsTradingInfo: {
          ...state.marketsTradingInfo,
          [action.market.market]: transformMarketSessionInfo(action.market),
        },
        marketsData: {
          ...state.marketsData,
          [action.market.market]: transformMarketData(action.market),
        },
      };
    }
    case 'LOAD_MARKETS': {
      const pinnedPositions: ListedMarketsState['marketsPinPosition'] = {};
      const tickSizes: ListedMarketsState['tickSizes'] = {
        // 'DIL-USD': calculateTickSize('0.00000001'),
        // 'DIL-USD': calculateTickSize('0.001'),
      };
      const stepSizes: ListedMarketsState['stepSizes'] = {};
      const takerOrderMinimums: ListedMarketsState['takerOrderMinimums'] = {};
      const maximumPositionSizes: ListedMarketsState['maximumPositionSizes'] =
        {};
      const marketsLeverageParams: ListedMarketsState['marketsLeverageParams'] =
        {};
      const marketsTradingInfo: ListedMarketsState['marketsTradingInfo'] = {};
      const marketsData: ListedMarketsState['marketsData'] = {};

      const newList = action.markets.map(market => {
        if (market.pinPosition) {
          pinnedPositions[market.market] = market.pinPosition;
        }
        // To prevent re-renders when loading
        // tickSizes[market.market] = calculateTickSize('0.00000001');
        if (market.tickSize) {
          tickSizes[market.market] = calculateTickOrStepSize(market.tickSize);
        }
        if (market.stepSize) {
          stepSizes[market.market] = calculateTickOrStepSize(market.stepSize);
        }
        takerOrderMinimums[market.market] = market.takerOrderMinimum;
        maximumPositionSizes[market.market] = market.maximumPositionSize;
        marketsLeverageParams[market.market] =
          transformMarketLeverageParams(market);
        marketsTradingInfo[market.market] = transformMarketSessionInfo(market);
        marketsData[market.market] = transformMarketData(market);
        return market.market;
      });

      const newSet = new Set(
        (action.markets || [])
          .map(market => [market.baseAsset, market.quoteAsset])
          .flat()
      );
      // Spreading the previous maps first keeps hidden (unlisted) markets
      // loaded via LOAD_HIDDEN_MARKET across reloads; stabilizeMapEntries
      // keeps entry references stable when a reload returns equal data
      return {
        defaultMarket: action.defaultMarket || '',
        loadedForLoggedIn: action.loggedIn,
        loadedAtMs: action.loadedAtMs,
        listedMarketsList: arraysEqual(newList, state.listedMarketsList)
          ? state.listedMarketsList
          : newList,
        listedTokensSet:
          state.listedTokensSet && setEqual(newSet, state.listedTokensSet)
            ? state.listedTokensSet
            : newSet,
        marketsPinPosition: stabilizeMapEntries(
          state.marketsPinPosition ?? {},
          pinnedPositions
        ),
        tickSizes: stabilizeMapEntries(state.tickSizes, {
          ...state.tickSizes,
          ...tickSizes,
        }),
        stepSizes: stabilizeMapEntries(state.stepSizes, {
          ...state.stepSizes,
          ...stepSizes,
        }),
        takerOrderMinimums: stabilizeMapEntries(state.takerOrderMinimums, {
          ...state.takerOrderMinimums,
          ...takerOrderMinimums,
        }),
        maximumPositionSizes: stabilizeMapEntries(state.maximumPositionSizes, {
          ...state.maximumPositionSizes,
          ...maximumPositionSizes,
        }),
        marketsLeverageParams: stabilizeMapEntries(
          state.marketsLeverageParams ?? {},
          {
            ...state.marketsLeverageParams,
            ...marketsLeverageParams,
          }
        ),
        marketsTradingInfo: stabilizeMapEntries(
          state.marketsTradingInfo ?? {},
          {
            ...state.marketsTradingInfo,
            ...marketsTradingInfo,
          }
        ),
        marketsData: stabilizeMapEntries(state.marketsData, {
          ...state.marketsData,
          ...marketsData,
        }),
        // enabledTradingMarkets: new Set(
        //   action.markets
        //     .filter(
        //       market =>
        //         market.status !== 'inactive' && market.status !== 'cancelsOnly'
        //     )
        //     .map(market => market.market)
        // ),
        // enabledCancelsMarkets: new Set(
        //   action.markets
        //     .filter(market => market.status !== 'inactive')
        //     .map(market => market.market)
        // ),
        // limitMakerOnlyMarketsSet: new Set(
        //   action.markets
        //     .filter(market => market.status === 'limitMakerOnly')
        //     .map(market => market.market)
        // ),
      };
    }
    case 'DEBUG_UPDATE_LISTED_MARKETS_STATUS':
      return {
        ...state,
        // limitMakerOnlyMarketsSet: new Set(
        //   action.status === 'limitMakerOnly' ? state.listedMarketsList : []
        // ),
      };
    default:
      return state;
  }
};
